import React, { useState, useEffect, useCallback } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import SyncIcon from '@mui/icons-material/Sync';
import { useAuth } from '../contexts/AuthContext';
import { useComuneConfig } from '../contexts/ConfigContext';
import { orariUfficiService, OrarioRow, OrarioTipo } from '../services/orariUfficiService';

// ─── Costanti ────────────────────────────────────────────────────────────────

const DAY_ORDER = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

const DAY_NAMES: Record<string, string> = {
  mon: 'Lunedì',
  tue: 'Martedì',
  wed: 'Mercoledì',
  thu: 'Giovedì',
  fri: 'Venerdì',
  sat: 'Sabato',
  sun: 'Domenica',
};

// Ordine e aspetto delle sezioni di ogni card, una per tipo di orario.
const TIPO_ORDER: OrarioTipo[] = ['apertura', 'appuntamento', 'telefonico'];

const TIPO_META: Record<OrarioTipo, { label: string; color: 'success' | 'warning' | 'info' }> = {
  apertura:     { label: 'Apertura al pubblico', color: 'success' },
  appuntamento: { label: 'Su appuntamento',      color: 'warning' },
  telefonico:   { label: 'Telefonico',           color: 'info' },
};

// ─── Tipi ─────────────────────────────────────────────────────────────────────

interface DaySlots {
  morning?: OrarioRow;
  afternoon?: OrarioRow;
}

interface OfficeData {
  area: string | null;
  tipi: Map<OrarioTipo, Map<string, DaySlots>>;
}

interface DialogState {
  open: boolean;
  mode: 'edit' | 'create';
  row: OrarioRow | null;       // edit mode
  ufficio_nome: string;        // create mode context
  giorno: string;              // create mode context
  fascia: string;              // create mode context
  tipo: OrarioTipo;            // create mode context
  apertura: string;
  chiusura: string;
}

const DIALOG_CLOSED: DialogState = {
  open: false, mode: 'edit', row: null,
  ufficio_nome: '', giorno: '', fascia: '', tipo: 'apertura',
  apertura: '08:30', chiusura: '13:30',
};

// ─── Helper: raggruppa i dati per ufficio ─────────────────────────────────────

function groupRows(rows: OrarioRow[]): Map<string, OfficeData> {
  const map = new Map<string, OfficeData>();
  for (const row of rows) {
    if (!map.has(row.ufficio_nome)) {
      map.set(row.ufficio_nome, { area: row.area, tipi: new Map() });
    }
    const office = map.get(row.ufficio_nome)!;
    const tipo = row.tipo ?? 'apertura';
    if (!office.tipi.has(tipo)) office.tipi.set(tipo, new Map());
    const days = office.tipi.get(tipo)!;
    if (!days.has(row.giorno)) days.set(row.giorno, {});
    const day = days.get(row.giorno)!;
    if (row.fascia === 'morning') day.morning = row;
    else if (row.fascia === 'afternoon') day.afternoon = row;
  }
  return map;
}

// ─── Sottocomponente: singola cella fascia ─────────────────────────────────────

interface SlotCellProps {
  row: OrarioRow | undefined;
  canEdit: boolean;
  ufficio_nome: string;
  giorno: string;
  fascia: string;
  tipo: OrarioTipo;
  onEdit: (s: DialogState) => void;
  onDelete: (row: OrarioRow) => void;
}

const SlotCell: React.FC<SlotCellProps> = ({
  row, canEdit, ufficio_nome, giorno, fascia, tipo, onEdit, onDelete,
}) => {
  if (!row) {
    return (
      <TableCell align="center" sx={{ color: 'text.disabled', fontSize: '0.8rem' }}>
        {canEdit ? (
          <Tooltip title="Aggiungi fascia">
            <IconButton
              size="small"
              onClick={() =>
                onEdit({
                  open: true, mode: 'create', row: null,
                  ufficio_nome, giorno, fascia, tipo,
                  apertura: fascia === 'morning' ? '08:30' : '15:00',
                  chiusura: fascia === 'morning' ? '13:30' : '18:00',
                })
              }
              sx={{ opacity: 0.4, '&:hover': { opacity: 1 } }}
            >
              <AddIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        ) : (
          '—'
        )}
      </TableCell>
    );
  }

  return (
    <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}>
        <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
          {row.apertura}–{row.chiusura}
        </Typography>
        {canEdit && (
          <>
            <Tooltip title="Modifica">
              <IconButton
                size="small"
                onClick={() =>
                  onEdit({
                    open: true, mode: 'edit', row,
                    ufficio_nome: row.ufficio_nome,
                    giorno: row.giorno, fascia: row.fascia, tipo: row.tipo,
                    apertura: row.apertura, chiusura: row.chiusura,
                  })
                }
                sx={{ p: 0.25 }}
              >
                <EditIcon sx={{ fontSize: 13 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Elimina">
              <IconButton size="small" onClick={() => onDelete(row)} sx={{ p: 0.25, color: 'error.light' }}>
                <DeleteIcon sx={{ fontSize: 13 }} />
              </IconButton>
            </Tooltip>
          </>
        )}
      </Box>
    </TableCell>
  );
};

// ─── Sottocomponente: card ufficio ─────────────────────────────────────────────

interface OfficeCardProps {
  nome: string;
  data: OfficeData;
  canEdit: boolean;
  onEdit: (s: DialogState) => void;
  onDelete: (row: OrarioRow) => void;
}

const OfficeCard: React.FC<OfficeCardProps> = ({ nome, data, canEdit, onEdit, onDelete }) => {
  // Sezioni: i tipi con almeno una fascia; in modifica anche l'apertura vuota,
  // così si può aggiungere la prima fascia.
  const tipi = TIPO_ORDER.filter((t) => data.tipi.has(t) || (canEdit && t === 'apertura'));

  return (
    <Card variant="outlined" sx={{ height: '100%' }}>
      <CardContent sx={{ pb: '12px !important' }}>
        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
          {nome}
        </Typography>
        {data.area && (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
            {data.area}
          </Typography>
        )}
        <Divider sx={{ mb: 1 }} />

        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', pl: 0 }}>Giorno</TableCell>
              <TableCell align="center" sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Mattina</TableCell>
              <TableCell align="center" sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Pomeriggio</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {tipi.map((tipo) => {
              const days = data.tipi.get(tipo) ?? new Map<string, DaySlots>();
              // Giorni da mostrare: quelli con almeno uno slot + in modifica i giorni lavorativi
              const visibleDays = DAY_ORDER.filter(
                (d) => days.has(d) || (canEdit && tipo === 'apertura' && d !== 'sun'),
              );
              return (
                <React.Fragment key={tipo}>
                  <TableRow>
                    <TableCell colSpan={3} sx={{ pl: 0, pt: 1.5, pb: 0.5, borderBottom: 0 }}>
                      <Chip
                        size="small" variant="outlined" color={TIPO_META[tipo].color}
                        label={TIPO_META[tipo].label}
                        sx={{ fontWeight: 600, fontSize: '0.7rem' }}
                      />
                    </TableCell>
                  </TableRow>
                  {visibleDays.map((dayKey) => {
                    const slots = days.get(dayKey) ?? {};
                    return (
                      <TableRow key={dayKey}>
                        <TableCell sx={{ fontSize: '0.8rem', pl: 0, color: 'text.secondary' }}>
                          {DAY_NAMES[dayKey] ?? dayKey}
                        </TableCell>
                        <SlotCell
                          row={slots.morning} canEdit={canEdit}
                          ufficio_nome={nome} giorno={dayKey} fascia="morning" tipo={tipo}
                          onEdit={onEdit} onDelete={onDelete}
                        />
                        <SlotCell
                          row={slots.afternoon} canEdit={canEdit}
                          ufficio_nome={nome} giorno={dayKey} fascia="afternoon" tipo={tipo}
                          onEdit={onEdit} onDelete={onDelete}
                        />
                      </TableRow>
                    );
                  })}
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
};

// ─── Pagina principale ────────────────────────────────────────────────────────

const OrariUfficiPage: React.FC = () => {
  const { user } = useAuth();
  const { officeHoursSource } = useComuneConfig();
  const isAdmin = user?.role === 'ADMIN';
  // Editing only affects the assistant when the source is DB-backed (static_db);
  // for live_api comuni the tool re-fetches the site, so the page stays read-only.
  const canEdit = isAdmin && officeHoursSource === 'static_db';

  const [rows, setRows]       = useState<OrarioRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');
  const [search, setSearch]   = useState('');

  const [dialog, setDialog]   = useState<DialogState>(DIALOG_CLOSED);
  const [saving, setSaving]   = useState(false);

  const [deleteRow, setDeleteRow]     = useState<OrarioRow | null>(null);
  const [deleting, setDeleting]       = useState(false);

  const [snack, setSnack] = useState<{ open: boolean; msg: string; severity: 'success' | 'error' }>({
    open: false, msg: '', severity: 'success',
  });

  const showSnack = (msg: string, severity: 'success' | 'error' = 'success') =>
    setSnack({ open: true, msg, severity });

  // ─── Fetch ────────────────────────────────────────────────────────────────

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setRows(await orariUfficiService.getAll());
    } catch {
      setError('Impossibile caricare gli orari. Riprova più tardi.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ─── Sincronizza ────────────────────────────────────────────────────────────

  const handleSync = async () => {
    try {
      const res = await orariUfficiService.sync();
      showSnack(`Sync OK: ${res.inserted} inseriti, ${res.updated} aggiornati, ${res.skipped} invariati`);
      fetchData();
    } catch {
      showSnack('Errore durante la sincronizzazione.', 'error');
    }
  };

  // ─── Salva (crea o modifica) ───────────────────────────────────────────────

  const handleSave = async () => {
    setSaving(true);
    try {
      if (dialog.mode === 'edit' && dialog.row) {
        const updated = await orariUfficiService.update(
          dialog.row.id, dialog.apertura, dialog.chiusura,
        );
        setRows((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));
        showSnack('Orario aggiornato.');
      } else {
        const created = await orariUfficiService.create({
          ufficio_nome: dialog.ufficio_nome,
          giorno: dialog.giorno,
          fascia: dialog.fascia,
          tipo: dialog.tipo,
          apertura: dialog.apertura,
          chiusura: dialog.chiusura,
        });
        setRows((prev) => [...prev, created]);
        showSnack('Fascia aggiunta.');
      }
      setDialog(DIALOG_CLOSED);
    } catch {
      showSnack('Errore durante il salvataggio.', 'error');
    } finally {
      setSaving(false);
    }
  };

  // ─── Elimina ──────────────────────────────────────────────────────────────

  const handleDelete = async () => {
    if (!deleteRow) return;
    setDeleting(true);
    try {
      await orariUfficiService.delete(deleteRow.id);
      setRows((prev) => prev.filter((r) => r.id !== deleteRow.id));
      showSnack('Fascia eliminata.');
      setDeleteRow(null);
    } catch {
      showSnack('Errore durante l\'eliminazione.', 'error');
    } finally {
      setDeleting(false);
    }
  };

  // ─── Filtraggio ───────────────────────────────────────────────────────────

  const grouped = groupRows(rows);
  const filteredEntries = Array.from(grouped.entries()).filter(([nome]) =>
    nome.toLowerCase().includes(search.toLowerCase()),
  );

  // ─── Render ───────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Box sx={{ mt: 4 }}><Alert severity="error">{error}</Alert></Box>;
  }

  const fasciaLabel = dialog.fascia === 'morning' ? 'Mattina' : 'Pomeriggio';
  const dialogTitle = dialog.mode === 'edit'
    ? `Modifica orario`
    : `Aggiungi fascia`;

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h5" fontWeight={700}>Orari degli Uffici</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <TextField
            size="small"
            placeholder="Cerca ufficio…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 220 }}
          />
          <Button startIcon={<SyncIcon />} variant="outlined" onClick={handleSync}>
            Sincronizza orari
          </Button>
        </Box>
      </Box>

      {/* Griglia card */}
      <Grid container spacing={2}>
        {filteredEntries.map(([nome, data]) => (
          <Grid item xs={12} sm={6} lg={4} key={nome}>
            <OfficeCard
              nome={nome} data={data} canEdit={canEdit}
              onEdit={setDialog} onDelete={setDeleteRow}
            />
          </Grid>
        ))}
        {filteredEntries.length === 0 && (
          <Grid item xs={12}>
            <Typography color="text.secondary">Nessun ufficio trovato.</Typography>
          </Grid>
        )}
      </Grid>

      {/* Dialog crea / modifica */}
      <Dialog open={dialog.open} onClose={() => setDialog(DIALOG_CLOSED)} maxWidth="xs" fullWidth>
        <DialogTitle>
          {dialogTitle}
          <Typography variant="body2" color="text.secondary">
            {dialog.ufficio_nome} · {TIPO_META[dialog.tipo].label} · {DAY_NAMES[dialog.giorno] ?? dialog.giorno} · {fasciaLabel}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
            <TextField
              label="Apertura"
              type="time"
              value={dialog.apertura}
              onChange={(e) => setDialog((d) => ({ ...d, apertura: e.target.value }))}
              inputProps={{ step: 300 }}
              fullWidth
            />
            <TextField
              label="Chiusura"
              type="time"
              value={dialog.chiusura}
              onChange={(e) => setDialog((d) => ({ ...d, chiusura: e.target.value }))}
              inputProps={{ step: 300 }}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(DIALOG_CLOSED)} disabled={saving}>Annulla</Button>
          <Button onClick={handleSave} variant="contained" disabled={saving}>
            {saving ? <CircularProgress size={18} /> : 'Salva'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog conferma eliminazione */}
      <Dialog open={Boolean(deleteRow)} onClose={() => setDeleteRow(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Elimina fascia oraria</DialogTitle>
        <DialogContent>
          {deleteRow && (
            <Typography>
              Eliminare la fascia <strong>{deleteRow.fascia === 'morning' ? 'mattina' : 'pomeriggio'}</strong> di{' '}
              <strong>{DAY_NAMES[deleteRow.giorno] ?? deleteRow.giorno}</strong>
              {' '}({TIPO_META[deleteRow.tipo ?? 'apertura'].label.toLowerCase()}) per{' '}
              <strong>{deleteRow.ufficio_nome}</strong>?
              <br />
              <Typography component="span" variant="body2" color="text.secondary">
                {deleteRow.apertura}–{deleteRow.chiusura}
              </Typography>
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteRow(null)} disabled={deleting}>Annulla</Button>
          <Button onClick={handleDelete} color="error" variant="contained" disabled={deleting}>
            {deleting ? <CircularProgress size={18} /> : 'Elimina'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snack.severity} onClose={() => setSnack((s) => ({ ...s, open: false }))}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default OrariUfficiPage;
